// ============================================================================
// DUPLICATED LOGIC: also implemented in backend/app/jobs/model/retest_decile.py.
// Change both and update the parity fixtures in shared/vectors/retest_decile.json.
// Why: the public API classifies behavior-retest zones on the server (ADR 0005).
// CI runs the same fixtures in frontend/src/lib/retestDecile.vectors.test.ts and
// backend/worker_tests/test_retest_decile_vectors.py. A change to one side alone
// fails CI.
// ============================================================================
// ZONES ARE STAMPED, NEVER DISSOLVED.
import { api } from "../api";
import { stackCellData } from "./stackClient";
import { labelsFromServedZones } from "./zoneDesign";
import {
  decileClassIndex,
  decileCode,
  decileLabel,
  decileOf,
  rescaleDrivers,
} from "./behaviorZones";
import type { FieldCovariate } from "../types/contract";

export function resolveBehaviorDriverKeys(
  catalog: Pick<FieldCovariate, "key" | "has_values">[],
): {
  mKey: string;
  pKey: string;
  fKey: string;
} {
  const has = (k: string) =>
    catalog.some((c) => c.key === k && c.has_values !== false);
  const mKey = has("diffusion__corr_k_variance")
    ? "diffusion__corr_k_variance"
    : "diffusion__pre_k_variance";
  const pKey = "productivity__index_vs_county";
  const fKey = has("fertility__index_post")
    ? "fertility__index_post"
    : "fertility__index_pre";
  const missing = [mKey, pKey, fKey].filter((k) => !has(k));
  if (missing.length)
    throw new Error(
      "The moisture / productivity / fertility layers aren’t built on this field yet — run the covariate build first.",
    );
  return { mKey, pKey, fKey };
}

export interface ZoneDecileSummary {
  zone: number;
  code: string | null;
  label: string | null;
  mean: {
    moisture: number | null;
    productivity: number | null;
    fertility: number | null;
  };
}

/*
 *
 * Average the three index surfaces per zone and stamp each zone with its decile code.
 *
 * Returns the SAME features, in the SAME order, with `zone`/`code`/`label`/`mean_*` stamped —
 * `withZoneNames` (the save path) reads `code`, so the decile becomes the saved zone name with no
 * further glue. A zone that ends up with no readable cells keeps its original properties un-coded
 * rather than being invented a class.
 */
const DECILE_DIMS = ["moisture", "productivity", "fertility"] as const;

export function summarizeDecileZones(
  labels: number[],
  drivers: {
    moisture: (number | null)[];
    productivity: (number | null)[];
    fertility: (number | null)[];
  },
  zoneCount: number,
): ZoneDecileSummary[] {
  const sums = DECILE_DIMS.map(() => new Array<number>(zoneCount + 1).fill(0));
  const counts = DECILE_DIMS.map(() =>
    new Array<number>(zoneCount + 1).fill(0),
  );
  for (let i = 0; i < labels.length; i++) {
    const zone = labels[i]!;
    if (zone < 1 || zone > zoneCount) continue;
    DECILE_DIMS.forEach((dim, index) => {
      const value = drivers[dim][i];
      if (value != null && Number.isFinite(value)) {
        sums[index]![zone] = sums[index]![zone]! + value;
        counts[index]![zone] = counts[index]![zone]! + 1;
      }
    });
  }
  return Array.from({ length: zoneCount }, (_, index) => {
    const zone = index + 1;
    const mean = DECILE_DIMS.map((_, dim) =>
      counts[dim]![zone]! > 0 ? sums[dim]![zone]! / counts[dim]![zone]! : null,
    );
    const deciles = mean.map((value) => decileOf(value));
    const classIndex = decileClassIndex(deciles[0]!, deciles[1]!, deciles[2]!);
    return {
      zone,
      code: classIndex == null ? null : decileCode(classIndex),
      label: classIndex == null ? null : decileLabel(classIndex),
      mean: {
        moisture: mean[0]!,
        productivity: mean[1]!,
        fertility: mean[2]!,
      },
    };
  });
}

export async function classifyZonesByDecile(
  fieldId: string,
  fc: GeoJSON.FeatureCollection,
): Promise<{ fc: GeoJSON.FeatureCollection; summaries: ZoneDecileSummary[] }> {
  const catalog = await api.fetchFieldCovariates(fieldId);
  const { mKey, pKey, fKey } = resolveBehaviorDriverKeys(catalog);
  const cell = await stackCellData(fieldId, [mKey, pKey, fKey]);
  // onto the absolute 0–100 percentile scale — the same rescale the zone editor classifies on
  const drivers = rescaleDrivers({
    moisture: cell.features[0]!,
    productivity: cell.features[1]!,
    fertility: cell.features[2]!,
  });

  const n = fc.features?.length ?? 0;
  if (n === 0) throw new Error("That zone layer has no zones in it.");
  const labels =
    n === 1
      ? cell.centroids.map(() => 1)
      : labelsFromServedZones(cell.centroids, fc);
  if (!labels)
    throw new Error(
      "Those zones don’t line up with this field’s cell grid — pick a layer drawn on this field.",
    );

  const summaries = summarizeDecileZones(labels, drivers, n);
  const features = (fc.features ?? []).map((f, i) => {
    const summary = summaries[i]!;
    const { code, label } = summary;
    const mean = summary.mean;
    const p = {
      ...(f.properties ?? {}),
      zone: summary.zone,
      mean_moisture: mean.moisture,
      mean_productivity: mean.productivity,
      mean_fertility: mean.fertility,
    };
    return { ...f, properties: code == null ? p : { ...p, code, label } };
  });
  return { fc: { ...fc, features }, summaries };
}
